import { createContext, useContext, useEffect, useMemo } from 'react';

import Head from 'next/head';

import {
  DefaultTheme,
  ThemeProvider as StyledComponentsThemeProvider,
} from 'styled-components';
import { setCookie } from 'nookies';
import hasEqualValues from 'lodash/isEqual';

import { cookies } from '~/shared/constants/cookies';

import { useAuth } from '~/modules/auth/hooks/useAuth';

import { WithChildren } from '~/shared/types/WithChildren';
import { IResaleTheme } from '~/modules/resales/interfaces/IResaleTheme';
import { IResale } from '~/modules/resales/interfaces/IResale';

import { theme as baseTheme } from '~/shared/styles/theme';

export type ITheme = DefaultTheme & Omit<IResaleTheme, 'colors'>;

type IThemeContextData = ITheme;

interface IThemeProviderProps {
  defaultTheme: IThemeContextData;
}

const ThemeContext = createContext({} as IThemeContextData);

const ThemeProvider: WithChildren<IThemeProviderProps> = ({
  children,
  defaultTheme = {} as ITheme,
}) => {
  const { user, currentAccessResale } = useAuth();

  const whiteLabelResale: IResale =
    currentAccessResale?.whiteLabel || currentAccessResale || ({} as IResale);
  const hasWhiteLabel = Object.values(whiteLabelResale).length > 0;

  const themeSource =
    hasWhiteLabel && Object.keys(whiteLabelResale?.theme)?.length > 0
      ? whiteLabelResale?.theme
      : defaultTheme;
  const whiteLabelResaleColors = whiteLabelResale?.theme?.colors;

  const formattedTheme = useMemo(() => {
    return {
      ...baseTheme,
      ...(themeSource || {}),
      colors: {
        ...baseTheme.colors,
        ...(defaultTheme?.colors || {}),
        ...(whiteLabelResale?.theme?.colors && {
          primary: whiteLabelResaleColors.primary,
          onPrimary: whiteLabelResaleColors.onPrimary,
          secondary: whiteLabelResaleColors.secondary,
          onSecondary: whiteLabelResaleColors.onSecondary,
          terciary: whiteLabelResaleColors.terciary,
          onTerciary: whiteLabelResaleColors.onTerciary,
        }),
      },
      ...(hasWhiteLabel && {
        title:
          user?.title || `Portal da Revenda | ${currentAccessResale?.name}`,
        description:
          user?.description ||
          `Portal da Revenda ${
            user?.title || currentAccessResale?.name
          }, criada pela RS Solutions com o propósito de ser o maior o ecossistema de vendas do Brasil, integrando as vendas do meio físico e online numa única plataforma.`,
      }),
    };
  }, [
    defaultTheme,
    hasWhiteLabel,
    user?.description,
    currentAccessResale?.name,
    user?.title,
    whiteLabelResale.theme,
  ]);

  useEffect(() => {
    const isSameTheme = hasEqualValues(defaultTheme, formattedTheme);
    if (isSameTheme) return;

    setCookie(null, cookies.DEFAULT_THEME, JSON.stringify(formattedTheme), {
      path: '/',
    });
  }, [defaultTheme, formattedTheme]);

  const contextData: IThemeContextData = useMemo(
    () => ({
      ...(formattedTheme as ITheme),
    }),
    [formattedTheme]
  );

  return (
    <ThemeContext.Provider value={contextData}>
      <StyledComponentsThemeProvider theme={formattedTheme}>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
          />
          <meta name="theme-color" content={contextData.colors.primary} />
          <meta
            name="description"
            content={
              contextData.description ||
              defaultTheme?.description ||
              'Criada pela RS Solutions com o propósito de ser o maior o ecossistema de vendas do Brasil, integrando as vendas do meio físico e online numa única plataforma.'
            }
          />
          <meta
            property="og:image"
            content={
              contextData.socialMediaLogo || defaultTheme?.socialMediaLogo
            }
          />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="620" />

          <link
            rel="icon"
            type="image/x-icon"
            href={contextData.favicon || '/favicon-32x32.png'}
          />

          <title>
            {contextData.title ||
              defaultTheme?.title ||
              'Portal do Administrador | SERU'}
          </title>
        </Head>

        {children}
      </StyledComponentsThemeProvider>
    </ThemeContext.Provider>
  );
};

const useTheme = (): IThemeContextData => {
  return useContext(ThemeContext);
};

export { ThemeProvider, useTheme };
